import { ResourceDeleteDialog } from '@/components/crud/ResourceDeleteDialog'
import {
  type ExtraDetailTab,
  ResourceDetailSheet
} from '@/components/crud/ResourceDetailSheet'
import { ResourceFieldRenderer } from '@/components/crud/ResourceFieldRenderer'
import { ResourceFormDialog } from '@/components/crud/ResourceFormDialog'
import { DataTable } from '@/components/data-table/DataTable'
import { useRecordSheet } from '@/components/record-sheet/context'
import { BulkActionBar } from '@/components/primitives/BulkActionBar'
import { ErrorState } from '@/components/primitives/ErrorState'
import { LoadingState } from '@/components/primitives/LoadingState'
import { PageHeader } from '@/components/primitives/PageHeader'
import { Button } from '@/components/ui/button'
import { useResourceList } from '@/hooks/useResourceList'
import { useResourceSchema } from '@/hooks/useResourceSchema'
import { getVisibleListFields } from '@/lib/field-categories'
import { queryClient } from '@/lib/query'
import { exportCsvUrl } from '@/lib/resources'
import { fetchResourceStats } from '@/lib/sheet'
import { useQuery } from '@tanstack/react-query'
import type {
  ResourceField,
  ResourceRow,
  ResourceSchema
} from '@/lib/resources'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronLeft, ChevronRight, Download, Plus, Trash2 } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

export interface ResourceCrudPageProps {
  resource: string // backend key, e.g. "social__post"
  channelGroup?: string // WS group for live invalidation
  detailExtraTabs?: ExtraDetailTab[] // appended after the auto Detail tab
  rowActions?: (row: ResourceRow, helpers: { refetch: () => void }) => ReactNode
  /** Slot rendered to the left of "Export CSV" / "+ New" in the page header. */
  headerActions?: ReactNode
  /** Override page title (defaults to schema.label). */
  title?: string
  description?: string
  /** Keep the older centered detail dialog instead of the record sheet. */
  classicSheet?: boolean
}

function buildColumns(schema: ResourceSchema): ColumnDef<ResourceRow>[] {
  const allFields = [...schema.detail_fields, ...schema.form_fields]
  const visible = getVisibleListFields(schema.list_fields, allFields)
  return visible.map((name) => {
    const field: ResourceField | undefined =
      schema.detail_fields.find((f) => f.name === name) ??
      schema.form_fields.find((f) => f.name === name)

    const headerLabel = field?.label ?? name
    return {
      id: name,
      accessorKey: name,
      header: headerLabel,
      cell: ({ row }) => {
        const value = row.original[name]
        if (!field)
          return value === null || value === undefined ? '—' : String(value)
        return (
          <ResourceFieldRenderer
            field={field}
            value={value}
            resourceKey={schema.key}
            rowId={row.original.id as string | number}
          />
        )
      }
    } satisfies ColumnDef<ResourceRow>
  })
}

export function ResourceCrudPage({
  resource,
  channelGroup,
  detailExtraTabs,
  rowActions,
  headerActions,
  title,
  description,
  classicSheet = false
}: ResourceCrudPageProps) {
  const { openRecord } = useRecordSheet()
  const schemaQuery = useResourceSchema(resource)
  const schema = schemaQuery.data
  const stats = useQuery({
    queryKey: ['admin-resource-stats', resource],
    queryFn: () => fetchResourceStats(resource),
    staleTime: 60_000
  })

  const [search, setSearch] = useState('')
  const [selection, setSelection] = useState<Record<string, boolean>>({})
  const [activeRow, setActiveRow] = useState<ResourceRow | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ResourceRow | undefined>(undefined)
  const [deleteIds, setDeleteIds] = useState<Array<string | number>>([])

  // Server-side cursor pagination. Stack of cursors so Prev/Next work cleanly.
  const [cursorStack, setCursorStack] = useState<string[]>([])
  const cursor =
    cursorStack.length > 0 ? cursorStack[cursorStack.length - 1] : undefined

  // Reset cursor stack whenever search or resource changes — otherwise stale
  // cursors point into a different result set.
  useEffect(() => {
    setCursorStack([])
  }, [search, resource])

  const list = useResourceList(resource, {
    search,
    pageSize: 50,
    cursor,
    channel: channelGroup
  })

  const rows = list.data?.results ?? []

  const columns = useMemo<ColumnDef<ResourceRow>[]>(() => {
    if (!schema) return []
    return buildColumns(schema)
  }, [schema])

  const selectedIds = useMemo(() => {
    return Object.entries(selection)
      .filter(([, v]) => v)
      .map(([k]) => k as string)
  }, [selection])

  function refresh() {
    queryClient.invalidateQueries({
      queryKey: ['admin-resource-list', resource]
    })
  }

  if (schemaQuery.isLoading) {
    return <LoadingState variant="form" />
  }

  if (schemaQuery.error || !schema) {
    return (
      <ErrorState
        error={schemaQuery.error ?? 'Schema not available'}
        onRetry={() => schemaQuery.refetch()}
      />
    )
  }

  const exportHref = exportCsvUrl(resource, search ? { search } : {})

  return (
    <div className="space-y-4">
      <PageHeader
        title={title ?? schema.label}
        description={description ?? schema.description}
        actions={
          <>
            {headerActions}
            <Button asChild variant="outline" size="sm">
              <a href={exportHref} target="_blank" rel="noreferrer">
                <Download className="size-3.5" /> Export CSV
              </a>
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditing(undefined)
                setFormOpen(true)
              }}
            >
              <Plus className="size-3.5" /> New {schema.singular}
            </Button>
          </>
        }
      />

      {stats.isLoading && (
        <div className="h-[86px] animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
      )}
      {stats.data && stats.data.stats.length > 0 && (
        <div className="grid grid-cols-2 overflow-hidden rounded-[36px] border border-neutral-200 bg-white sm:grid-cols-3 xl:grid-cols-6 dark:border-neutral-800 dark:bg-neutral-900">
          {stats.data.stats.map((s) => (
            <div key={s.label} className="px-5 py-4" title={s.hint}>
              <p className="truncate text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
                {s.value}
              </p>
              <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <DataTable<ResourceRow>
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        isFetching={list.isFetching}
        error={list.error}
        onRetry={() => list.refetch()}
        getRowId={(row) => String(row.id ?? '')}
        onRowClick={(row) =>
          classicSheet
            ? setActiveRow(row)
            : openRecord(resource, row.id as string | number)
        }
        selection={selection}
        onSelectionChange={setSelection}
        enableSelection
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={`Search ${schema.label.toLowerCase()}…`}
        emptyTitle={`No ${schema.label.toLowerCase()} yet`}
        emptyDescription={`When ${schema.label.toLowerCase()} show up, you'll see them here.`}
        emptyAction={
          <Button
            size="sm"
            onClick={() => {
              setEditing(undefined)
              setFormOpen(true)
            }}
          >
            <Plus className="size-3.5" /> Create the first{' '}
            {schema.singular.toLowerCase()}
          </Button>
        }
        pageSize={50}
      />

      {/* Cursor pagination — backend returns next/previous URLs */}
      {(list.data?.next || list.data?.previous || cursorStack.length > 0) && (
        <div className="flex items-center justify-end gap-2 px-1 pt-3">
          <span className="mr-auto text-xs text-muted-foreground">
            {rows.length > 0
              ? `Showing ${rows.length} ${schema.label.toLowerCase()}`
              : ''}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={cursorStack.length === 0}
            onClick={() => setCursorStack((s) => s.slice(0, -1))}
          >
            <ChevronLeft className="size-3.5" /> Prev
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!list.data?.next}
            onClick={() => {
              const next = list.data?.next
              if (!next) return
              const url = new URL(next, window.location.origin)
              const nextCursor = url.searchParams.get('cursor')
              if (nextCursor) setCursorStack((s) => [...s, nextCursor])
            }}
          >
            Next <ChevronRight className="size-3.5" />
          </Button>
        </div>
      )}

      <BulkActionBar
        count={selectedIds.length}
        onClear={() => setSelection({})}
        noun={schema.singular.toLowerCase()}
        pluralNoun={schema.label.toLowerCase()}
        actions={
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={() => setDeleteIds(selectedIds)}
          >
            <Trash2 className="size-4" /> Delete selected
          </Button>
        }
      />

      <ResourceFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        schema={schema}
        initial={editing}
        onSaved={() => refresh()}
      />

      <ResourceDetailSheet
        open={Boolean(activeRow)}
        onOpenChange={(o) => !o && setActiveRow(null)}
        schema={schema}
        row={activeRow}
        extraTabs={detailExtraTabs}
        rowActions={
          activeRow && rowActions
            ? rowActions(activeRow, { refetch: refresh })
            : undefined
        }
        onEdit={() => {
          if (!activeRow) return
          setEditing(activeRow)
          setActiveRow(null)
          setFormOpen(true)
        }}
        onDelete={() => {
          if (!activeRow) return
          setDeleteIds([activeRow.id as string | number])
          setActiveRow(null)
        }}
      />

      <ResourceDeleteDialog
        open={deleteIds.length > 0}
        onOpenChange={(o) => !o && setDeleteIds([])}
        schema={schema}
        ids={deleteIds}
        onDeleted={() => {
          const removed = new Set(deleteIds.map(String))
          setDeleteIds([])
          setSelection((prev) => {
            const next = { ...prev }
            for (const id of removed) delete next[id]
            return next
          })
          refresh()
        }}
      />
    </div>
  )
}
